import { LinearGradient } from 'expo-linear-gradient'
import { Box, Center, Image, Row, Text, useTheme } from 'native-base'
import { StatusBar, StyleSheet } from 'react-native'
import Animated, {
  interpolate,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'

import {
  AbsoluteButtonWithIcon,
  Icon,
  LoadingState,
  OfferCodeBox,
  Spacer,
  WebBackButtonBar,
} from '~components'
import {
  detailsImgSizeVerticalLayout,
  detailsImgSizeHorizontalLayout,
  webNavBarHeight,
} from '~constants'
import { useEffect, useIsFocused, useMemo, useTranslation, useDeviceInfo } from '~hooks'
import { Images } from '~images'
import { useGetNftDetails } from '~query-hooks/useGetNfts'
import { formatDate } from '~utils'

const BACK_BUTTON_PADDING = 16

const AnimatedImage = Animated.createAnimatedComponent(Image)

export const NftDetailsScreen = ({
  route: {
    params: { nftId },
  },
  navigation: { canGoBack, goBack },
}: NftDetailsScreenProps) => {
  const isFocused = useIsFocused()
  const { colors } = useTheme()
  const { t } = useTranslation()
  const { top } = useSafeAreaInsets()
  const { isWeb, shouldApplyDesktopStyles } = useDeviceInfo()
  const { isLoadingNftDetails, nftDetails } = useGetNftDetails({ nftId })

  const topBackButtonOffset = useMemo(() => BACK_BUTTON_PADDING + top, [top])

  const sharedValue = useSharedValue(0)
  const scrollHandler = useAnimatedScrollHandler((event) => {
    const y = event.contentOffset.y
    sharedValue.value = y > -5 ? 1 : y
  }, [])

  const headerAnimatedStyles = useAnimatedStyle(() => {
    const scale = interpolate(sharedValue.value, [-500, 0], [6, 1.1])
    return { transform: [{ scale }] }
  })

  const imageAnimatedStyles = useAnimatedStyle(() => {
    const translateY = interpolate(sharedValue.value, [-500, 0], [-125, 0])
    return { transform: [{ translateY }] }
  })

  useEffect(() => {
    if (isFocused) {
      StatusBar.setBarStyle('light-content')
    } else {
      StatusBar.setBarStyle('dark-content')
    }
  }, [isFocused])

  const renderNFTImage = useMemo(
    () => (
      <AnimatedImage
        alt="NFT image"
        fallbackSource={Images.OFFER_IMAGE_PLACEHOLDER}
        resizeMode="cover"
        source={{ uri: nftDetails?.image }}
        style={[styles.image, imageAnimatedStyles]}
      />
    ),
    [imageAnimatedStyles, nftDetails?.image]
  )

  const renderNftDetailsContent = useMemo(
    () => (
      <Box
        bg={shouldApplyDesktopStyles ? 'white' : 'background'}
        {...(shouldApplyDesktopStyles && { flexGrow: 1 })}
      >
        <Center>
          <Box bg="actionBlue" roundedBottomLeft="lg" roundedBottomRight="lg" px="4" py="2">
            <Text color="white" fontWeight="500">
              {nftDetails?.name?.toUpperCase()}
            </Text>
          </Box>
        </Center>
        <Box px="4" pt="4">
          <Text fontSize="xl" fontWeight="700" {...(isWeb && { textAlign: 'center', mt: '16' })}>
            {t('nft_screen.nft_properties')}
          </Text>
          <Spacer y="4" />
          <Row>
            <InfoBox description={nftDetails?.type || ''} title={t('nft_screen.type')} />
            <Spacer x="4" />
            <InfoBox
              description={nftDetails?.brand || ''}
              logo={nftDetails?.logo}
              title={t('nft_screen.brand')}
            />
          </Row>
          <Spacer y="4" />
          <Row>
            <InfoBox description={nftDetails?.year || ''} title={t('nft_screen.year')} />
            <Spacer x="4" />
            <InfoBox
              description={
                nftDetails?.type === 'Membership'
                  ? t('nft_screen.no_exp_date')
                  : formatDate(nftDetails?.validUntil)
              }
              title={t('common.valid_until')}
            />
          </Row>
          <Spacer y="8" />
          <Center flexShrink={1}>
            {nftDetails?.type === 'Membership' ? (
              <Row
                alignItems="center"
                bg="amber.50"
                borderColor="amber.400"
                borderRadius="lg"
                borderWidth={1}
                px="16"
                py="4"
              >
                <Icon color="amber.400" name="vip-crown-2-fill" size={isWeb ? 20 : 24} />
                <Spacer x="4" />
                <Text color="amber.400" fontSize={isWeb ? '2xl' : '4xl'} fontWeight="700">
                  {t('nft_screen.gold')}
                </Text>
              </Row>
            ) : (
              <OfferCodeBox code={nftDetails?.id || '-'} solidBorder />
            )}
          </Center>
        </Box>
      </Box>
    ),
    [isWeb, nftDetails, shouldApplyDesktopStyles, t]
  )

  const renderDesktopContent = useMemo(
    () => (
      <>
        <WebBackButtonBar />
        {isLoadingNftDetails ? (
          <LoadingState />
        ) : (
          <Box pb={webNavBarHeight} my="auto">
            <Row h={detailsImgSizeHorizontalLayout} rounded="lg" overflow="hidden" bgColor="white">
              <Box w="65%">{renderNFTImage}</Box>
              <Box px="2" w={'35%'}>
                {renderNftDetailsContent}
              </Box>
            </Row>
          </Box>
        )}
      </>
    ),
    [isLoadingNftDetails, renderNftDetailsContent, renderNFTImage]
  )

  if (isLoadingNftDetails && !isWeb)
    return (
      <>
        <AbsoluteButtonWithIcon
          iconName="arrow-left-line"
          onClick={goBack}
          style={[styles.fabButton, { top: topBackButtonOffset }]}
        />
        <LoadingState />
      </>
    )

  if (shouldApplyDesktopStyles) return renderDesktopContent

  return (
    <SafeAreaView edges={['bottom']} style={styles.safeArea}>
      <Box h={`${top}px`} w="full" zIndex={100} position="absolute">
        <LinearGradient
          colors={[colors['gray']['600'], 'transparent']}
          style={[styles.gradientStyle]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
        />
      </Box>
      {canGoBack() ? (
        <AbsoluteButtonWithIcon
          iconName="arrow-left-line"
          onClick={goBack}
          style={[styles.fabButton, { top: topBackButtonOffset }]}
        />
      ) : null}
      <Animated.ScrollView
        contentContainerStyle={styles.scrollContainer}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        <Animated.View style={[styles.headerContainer, headerAnimatedStyles]}>
          {nftDetails?.image ? renderNFTImage : null}
        </Animated.View>
        {renderNftDetailsContent}
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

type InfoBoxProps = { description: string; logo?: number; title: string }

const InfoBox = ({ description, logo, title }: InfoBoxProps) => {
  const { isWeb, shouldApplyDesktopStyles } = useDeviceInfo()
  return (
    <Box
      alignItems="center"
      borderColor="text.300"
      borderWidth={1}
      flex={1}
      justifyContent="center"
      p="4"
      rounded="lg"
      {...(shouldApplyDesktopStyles && { px: '2', py: '3' })}
    >
      <Text color="text.500" fontSize={shouldApplyDesktopStyles ? 'xs' : 'sm'}>
        {title.toUpperCase()}
      </Text>
      <Spacer y="2" />
      <Row alignItems="center">
        {logo ? (
          <>
            <Image alt="brand logo" resizeMode="contain" size={8} source={logo} />
            <Spacer x="2" />
          </>
        ) : null}
        <Text fontSize={isWeb ? 'sm' : 'lg'} fontWeight="500" numberOfLines={1}>
          {description}
        </Text>
      </Row>
    </Box>
  )
}

const styles = StyleSheet.create({
  fabButton: { left: 24 },
  gradientStyle: { height: '100%' },

  headerContainer: {
    alignItems: 'center',
    height: detailsImgSizeVerticalLayout,
    justifyContent: 'center',
    width: '100%',
  },
  image: {
    height: '100%',
    width: '100%',
  },
  safeArea: { flexGrow: 1 },
  scrollContainer: { flexGrow: 1 },
})
